/**
 * IdentificationStep Component
 * Step 1: Basic identification fields for EPC
 *
 * @version 2025.1.0
 */

import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Box,
} from '@mui/material';
import { EpcFormData, TipoDeRisco, EpiEfficiency } from '../../../../../types/epc';
import { IdentificationStepProps } from '../EPCFormWizard.types';

export const IdentificationStep: React.FC<IdentificationStepProps> = ({
  disabled = false,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<EpcFormData>();

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Informe os dados básicos de identificação do EPC (Equipamento de Proteção Coletiva).
      </Typography>

      <Grid container spacing={3}>
        {/* Nome */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <Controller
            name="nome"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nome do EPC"
                fullWidth
                required
                error={Boolean(errors.nome)}
                helperText={errors.nome?.message || 'Ex: Enclausuramento de Máquinas'}
                disabled={disabled}
              />
            )}
          />
        </Grid>

        {/* Tipo de Risco */}
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <FormControl fullWidth required error={Boolean(errors.tipoDeRisco)}>
            <InputLabel>Tipo de Risco</InputLabel>
            <Controller
              name="tipoDeRisco"
              control={control}
              render={({ field }) => (
                <Select {...field} label="Tipo de Risco" disabled={disabled}>
                  <MenuItem value={TipoDeRisco.FISICO}>Físico</MenuItem>
                  <MenuItem value={TipoDeRisco.QUIMICO}>Químico</MenuItem>
                  <MenuItem value={TipoDeRisco.BIOLOGICO}>Biológico</MenuItem>
                  <MenuItem value={TipoDeRisco.ERGONOMICO}>Ergonômico</MenuItem>
                  <MenuItem value={TipoDeRisco.ACIDENTE}>Acidente</MenuItem>
                </Select>
              )}
            />
            {errors.tipoDeRisco && <FormHelperText>{errors.tipoDeRisco.message}</FormHelperText>}
          </FormControl>
        </Grid>

        {/* Risco */}
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Controller
            name="risco"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Descrição do Risco Controlado"
                fullWidth
                required
                error={Boolean(errors.risco)}
                helperText={errors.risco?.message || 'Ex: Partes móveis de máquinas'}
                disabled={disabled}
              />
            )}
          />
        </Grid>

        {/* Validade */}
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Controller
            name="validade"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Data de Validade"
                fullWidth
                required
                type="date"
                slotProps={{ inputLabel: { shrink: true } }}
                error={Boolean(errors.validade)}
                helperText={errors.validade?.message || 'Data de vencimento/manutenção'}
                disabled={disabled}
              />
            )}
          />
        </Grid>

        {/* EPC Eficaz / Não Eficaz (#305) */}
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Controller
            name="efficiency"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={Boolean(errors.efficiency)} disabled={disabled}>
                <InputLabel>Eficácia do EPC</InputLabel>
                <Select
                  {...field}
                  value={field.value ?? ''}
                  label="Eficácia do EPC"
                  onChange={(e) => field.onChange(e.target.value || undefined)}
                >
                  <MenuItem value="">Não informado</MenuItem>
                  <MenuItem value={EpiEfficiency.EFICAZ}>Eficaz</MenuItem>
                  <MenuItem value={EpiEfficiency.NAO_EFICAZ}>Não Eficaz</MenuItem>
                </Select>
                {errors.efficiency && (
                  <FormHelperText>{errors.efficiency.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default IdentificationStep;
